import type {ChangeEvent, ReactElement} from 'react';
import {useState} from 'react';

import {Button} from '@/components';
import {NumberInput, Select} from '@/components/common/Input';
import Tabs from '@/components/Tabs';
import type {
  BusinessLimit,
  BusinessLimitType,
  BusinessLimitValue,
} from '@/types';
import {BusinessLimitFrequency} from '@/types';
import {formatSelectOptionItem} from '@/utils/merchant';

type LimitMap = BusinessLimit['limit'];

interface LimitInputProps {
  onChange: (value: LimitMap) => void;
  value?: LimitMap;
  label?: string;
  dataCy?: string;
}

const typeOptions = [
  {key: formatSelectOptionItem('AMOUNT'), value: 'AMOUNT' as const},
  {key: formatSelectOptionItem('COUNT'), value: 'COUNT' as const},
];

const frequencyOrder: BusinessLimitFrequency[] = [
  BusinessLimitFrequency.Daily,
  BusinessLimitFrequency.Weekly,
  BusinessLimitFrequency.Monthly,
  BusinessLimitFrequency.Once,
];

const selectClassName =
  'w-full rounded-md border bg-transparent focus:outline-none focus:ring-indigo-500 focus:border-indigo-500';
const valueInputClassName =
  'w-full rounded-md border text-left [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

// A bound is only kept when it can be enforced: an empty input and 0 both mean
// "no bound", and a stored 0 max would block every transaction.
const withBounds = (bounds: BusinessLimitValue): BusinessLimitValue => ({
  ...(Number(bounds?.max) > 0 ? {max: Number(bounds.max)} : {}),
  ...(Number(bounds?.min) > 0 ? {min: Number(bounds.min)} : {}),
});

const hasBound = (value?: BusinessLimitValue): boolean =>
  Number(value?.max) > 0 || Number(value?.min) > 0;

const LimitInput = ({
  label,
  onChange,
  value,
  dataCy,
}: LimitInputProps): ReactElement => {
  const current = value ?? {};
  // `onChange` drops an entry while it has no bound, so a type picked before a
  // value would be lost and its select would snap back to the default. Keep
  // the last edit per frequency here until a bound arrives.
  const [drafts, setDrafts] = useState<LimitMap>({});

  const updateEntry = (
    frequency: BusinessLimitFrequency,
    patch: {type?: BusinessLimitType['type']} & BusinessLimitValue,
  ) => {
    const existing: BusinessLimitType = current[frequency] ??
      drafts[frequency] ?? {
        type: 'AMOUNT',
        value: {},
      };
    const next: BusinessLimitType = {
      type: patch.type ?? existing.type,
      value: withBounds({
        ...existing.value,
        ...(patch.max === undefined ? {} : {max: patch.max}),
        ...(patch.min === undefined ? {} : {min: patch.min}),
      }),
    };
    setDrafts({...drafts, [frequency]: next});
    const map: LimitMap = {...current, [frequency]: next};
    const cleaned = Object.fromEntries(
      Object.entries(map).filter(([, e]) => hasBound(e?.value)),
    ) as LimitMap;
    onChange(cleaned);
  };

  const clearEntry = (frequency: BusinessLimitFrequency) => {
    const map: LimitMap = {...current};
    delete map[frequency];
    const nextDrafts: LimitMap = {...drafts};
    delete nextDrafts[frequency];
    setDrafts(nextDrafts);
    onChange(map);
  };

  const renderFrequencyForm = (
    frequency: BusinessLimitFrequency,
  ): ReactElement => {
    const entry = current[frequency];
    const draft = entry ?? drafts[frequency];
    // The server measures an AMOUNT limit in USD and checks its min against a
    // single transaction; a COUNT limit's min is not checked.
    const isAmount = (draft?.type ?? 'AMOUNT') === 'AMOUNT';
    // A ONCE max is per transaction; the other frequencies cap a running total.
    let maxLabel = 'Max transactions';
    if (isAmount) {
      maxLabel =
        frequency === BusinessLimitFrequency.Once
          ? 'Max per transaction (USD)'
          : 'Max total (USD)';
    }
    return (
      <div className="flex flex-col gap-2">
        <Select
          dataCy={dataCy ? `${dataCy}-limit-type` : undefined}
          label="Limit Type"
          value={draft?.type ?? 'AMOUNT'}
          onChange={(e: ChangeEvent<HTMLSelectElement>) =>
            updateEntry(frequency, {
              type: e.target.value as BusinessLimitType['type'],
            })
          }
          options={typeOptions}
          bodyClassName={selectClassName}
        />
        <NumberInput
          data-cy={dataCy ? `${dataCy}-limit-max` : undefined}
          label={maxLabel}
          value={entry?.value?.max ?? ''}
          placeholder="0"
          className={valueInputClassName}
          onValueChange={(v) => updateEntry(frequency, {max: Number(v) || 0})}
        />
        <NumberInput
          data-cy={dataCy ? `${dataCy}-limit-min` : undefined}
          label={
            isAmount
              ? 'Min single transaction (USD)'
              : 'Min (not checked on count limits)'
          }
          value={entry?.value?.min ?? ''}
          placeholder="0"
          className={valueInputClassName}
          onValueChange={(v) => updateEntry(frequency, {min: Number(v) || 0})}
        />
        <Button
          type="button"
          dataCy={dataCy ? `${dataCy}-clear` : undefined}
          variant="outline"
          colorScheme="red"
          className="self-end"
          // A type picked before any bound is a draft worth clearing too.
          disabled={!entry && !drafts[frequency]}
          onClick={() => clearEntry(frequency)}
        >
          {`Clear ${formatSelectOptionItem(frequency)} limit`}
        </Button>
      </div>
    );
  };

  const tabs = frequencyOrder.map((frequency) => ({
    id: frequency,
    title: formatSelectOptionItem(frequency),
    // The frequency enum values are upper case (`ONCE`); the suite's ids are
    // lower case like every other limits id, so fold it here.
    dataCy: dataCy ? `${dataCy}-tab-${frequency.toLowerCase()}` : undefined,
    content: renderFrequencyForm(frequency),
  }));

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          {label}
        </label>
      )}
      <Tabs dataCy={dataCy} tabs={tabs} />
    </div>
  );
};

export default LimitInput;
